// src/components/dashboards/ GrievanceOfficerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../shared/DashboardLayout';
import { grievanceAPI } from '../../services/api';
import '../../styles/Grievance.css';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'PENDING',      label: 'Pending' },
  { value: 'ASSIGNED',     label: 'Assigned' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'ESCALATED',    label: 'Escalated' },
  { value: 'RESOLVED',     label: 'Resolved' },
  { value: 'CLOSED',       label: 'Closed' },
  { value: 'REJECTED',     label: 'Rejected' },
  { value: 'REOPENED',     label: 'Reopened' },
];

const STATUS_COLORS = {
  PENDING:      { badge: 'badge-pending',      dot: '#f59e0b' },
  ASSIGNED:     { badge: 'badge-assigned',     dot: '#3b82f6' },
  UNDER_REVIEW: { badge: 'badge-under_review', dot: '#8b5cf6' },
  RESOLVED:     { badge: 'badge-resolved',     dot: '#10b981' },
  REJECTED:     { badge: 'badge-rejected',     dot: '#ef4444' },
  ESCALATED:    { badge: 'badge-escalated',    dot: '#f97316' },
  CLOSED:       { badge: 'badge-closed',       dot: '#6b7280' },
  REOPENED:     { badge: 'badge-reopened',     dot: '#ec4899' },
};

const PRIORITY_COLORS = {
  LOW:    { badge: 'badge-low',    color: '#10b981' },
  MEDIUM: { badge: 'badge-medium', color: '#f59e0b' },
  HIGH:   { badge: 'badge-high',   color: '#ef4444' },
};

const StatTile = ({ label, value, color, onClick, active }) => (
  <div
    className="grievance-stat-card"
    onClick={onClick}
    style={{
      cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid #e5e7eb',
      transition: 'all 0.15s',
    }}
  >
    <div className="grievance-stat-number" style={{ color }}>{value}</div>
    <div className="grievance-stat-label">{label}</div>
  </div>
);

const GrievanceOfficerDashboard = () => {
  const navigate = useNavigate();

  const [stats,       setStats]       = useState(null);
  const [grievances,  setGrievances]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statsLoading,setStatsLoading]= useState(true);
  const [activeStatus,setActiveStatus]= useState('');
  const [search,      setSearch]      = useState('');
  const [priority,    setPriority]    = useState('');

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadGrievances(); }, [activeStatus, priority]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await grievanceAPI.getOfficerStats();
      setStats(data);
    } catch (e) { console.error(e); }
    finally { setStatsLoading(false); }
  };

  const loadGrievances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeStatus) params.status   = activeStatus;
      if (priority)     params.priority = priority;
      const data = await grievanceAPI.getAll(params);
      setGrievances(Array.isArray(data) ? data : data.results || []);
    } catch (e) { console.error(e); setGrievances([]); }
    finally { setLoading(false); }
  };

  const filtered = grievances.filter(g => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      g.grievance_id.toLowerCase().includes(q) ||
      g.subject.toLowerCase().includes(q) ||
      g.submitted_by?.full_name?.toLowerCase().includes(q) ||
      g.submitted_by?.username?.toLowerCase().includes(q)
    );
  });

  const timeAgo = (d) => {
    const s = Math.floor((Date.now() - new Date(d)) / 1000);
    if (s < 60)    return 'Just now';
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const statTiles = stats ? [
    { label: 'Total',       value: stats.total,        color: '#6366f1', status: '' },
    { label: 'Pending',     value: stats.pending,      color: '#f59e0b', status: 'PENDING' },
    { label: 'Assigned',    value: stats.assigned,     color: '#3b82f6', status: 'ASSIGNED' },
    { label: 'Under Review',value: stats.under_review, color: '#8b5cf6', status: 'UNDER_REVIEW' },
    { label: 'Escalated',   value: stats.escalated,    color: '#f97316', status: 'ESCALATED' },
    { label: 'Resolved',    value: stats.resolved,     color: '#10b981', status: 'RESOLVED' },
    { label: 'Closed',      value: stats.closed,       color: '#6b7280', status: 'CLOSED' },
    { label: 'High Priority',value: stats.high_priority,color: '#ef4444', status: '' },
  ] : [];

  return (
    <DashboardLayout title="Grievance Officer Dashboard">
      <div className="grievance-container">

        {/* Stats */}
        <div className="grievance-stats-grid">
          {statsLoading ? (
            [...Array(8)].map((_, i) => (
              <div key={i} className="grievance-stat-card" style={{ background: '#f3f4f6', border: 'none' }}>
                <div style={{ height: 32, background: '#e5e7eb', borderRadius: 4, marginBottom: 6 }} />
                <div style={{ height: 14, background: '#e5e7eb', borderRadius: 4, width: '60%', margin: '0 auto' }} />
              </div>
            ))
          ) : statTiles.map(t => (
            <StatTile
              key={t.label}
              label={t.label}
              value={t.value}
              color={t.color}
              active={t.status !== '' && activeStatus === t.status}
              onClick={t.status !== '' ? () => setActiveStatus(prev => prev === t.status ? '' : t.status) : undefined}
            />
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
            Grievances
            {filtered.length > 0 && <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.875rem', marginLeft: 8 }}>({filtered.length})</span>}
          </h2>
        </div>

        {/* Filters row */}
        <div className="grievance-filters">
          <input
            className="grievance-search"
            placeholder="Search by ID, subject, submitter…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(f => (
              <button key={f.value}
                className={`filter-chip ${activeStatus === f.value ? 'active' : ''}`}
                onClick={() => setActiveStatus(f.value)}>
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value)}
            style={{ padding: '0.375rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: 20, fontSize: '0.8rem', background: '#fff' }}
          >
            <option value="">All Priorities</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-large" />
            <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading grievances…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="grievance-empty">
            <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No grievances found</p>
            <span>Try adjusting the filters.</span>
          </div>
        ) : (
          filtered.map(g => {
            const sc = STATUS_COLORS[g.status] || { badge: 'badge-closed', dot: '#6b7280' };
            const pc = PRIORITY_COLORS[g.priority] || { badge: 'badge-medium', color: '#f59e0b' };
            return (
              <div
                key={g.id}
                className="grievance-card"
                onClick={() => navigate(`/grievances/${g.id}`)}
              >
                <div className="grievance-card-header">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap' }}>
                      <span className="grievance-id">{g.grievance_id}</span>
                      <span className={`grievance-badge ${sc.badge}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />
                        {g.status.replace('_', ' ')}
                      </span>
                      <span className={`grievance-badge ${pc.badge}`}>{g.priority}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                        {g.category_display}
                      </span>
                    </div>
                    <p className="grievance-subject" style={{ marginBottom: '0.25rem' }}>{g.subject}</p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                      By: <strong>{g.submitted_by?.full_name || g.submitted_by?.username}</strong>
                      {g.department_assigned && (
                        <> · Dept: <strong>{g.department_assigned.replace(/_/g, ' ')}</strong></>
                      )}
                      {g.assigned_to && (
                        <> · Assigned to: <strong>{g.assigned_to.full_name || g.assigned_to.username}</strong></>
                      )}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{timeAgo(g.updated_at)}</div>
                    {g.response_count > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 2 }}>
                        {g.response_count} response{g.response_count !== 1 ? 's' : ''}
                      </div>
                    )}
                    {/* Urgent indicator */}
                    {g.priority === 'HIGH' && !['CLOSED', 'RESOLVED'].includes(g.status) && (
                      <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600, marginTop: 2 }}>⚠ URGENT</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </DashboardLayout>
  );
};

export default GrievanceOfficerDashboard;